import { BackwardOutlined, ReloadOutlined } from "@ant-design/icons"
import { App, Button, Empty, Form } from "antd"
import React, {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { FormChangedWatcher } from "../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import {
  getBulkColumnChangeFields,
  getBulkFeaturesChangeFields,
  getValuesChangeFields
} from "../helpers/cascade.ts"
import type { NestedValues } from "../helpers/dependencies.ts"
import { type OnBulkChange, type OnFeaturesToggle } from "../types.ts"
import type { PermissionFeatures } from "../types/permissions.types.ts"
import { PermissionsEditor } from "./editor/PermissionsEditor.tsx"

export interface PermissionsFormRef {
  getIsDirty: () => boolean
}

interface PermissionsFormProps {
  permissions: PermissionFeatures | null
  saving?: boolean
  disabled?: boolean
  isSystem?: boolean
  roleName?: string
  roleUserCount?: number
  onTransferOwnership?: () => void
  getDefaultPermissions?: () => Promise<PermissionFeatures | undefined>
  formRef?: Ref<PermissionsFormRef>
  onFinish?: (values: PermissionFeatures) => void
}

export function PermissionsForm({
  saving,
  disabled,
  isSystem,
  roleName,
  roleUserCount,
  onTransferOwnership,
  getDefaultPermissions,
  permissions,
  formRef,
  onFinish
}: PermissionsFormProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isDirty, setIsDirty] = useState(false)
  const { modal, notification } = App.useApp()

  useImperativeHandle<PermissionsFormRef, PermissionsFormRef>(formRef, () => ({
    getIsDirty: () => isDirty
  }))

  const onBulkColumnChange: OnBulkChange = useCallback(
    (parentKey, colKey, subKeys, value) => {
      const allValues = form.getFieldsValue(true) as NestedValues
      const fields = getBulkColumnChangeFields(
        parentKey as string,
        colKey,
        subKeys,
        value,
        allValues
      )
      if (fields.length > 0) form.setFields(fields)
    },
    [form]
  )

  const onFeaturesToggleChange: OnFeaturesToggle = useCallback(
    (parentKey, features, value) => {
      const allValues = form.getFieldsValue(true) as NestedValues
      const fields = getBulkFeaturesChangeFields(
        parentKey as string,
        features,
        value,
        allValues
      )
      if (fields.length > 0) form.setFields(fields)
    },
    [form]
  )

  const handleValuesChange = useCallback(
    (changedValues: Partial<PermissionFeatures>) => {
      const allValues = form.getFieldsValue(true) as NestedValues
      const fields = getValuesChangeFields(changedValues, allValues)
      if (fields.length > 0) form.setFields(fields)
    },
    [form]
  )

  const handleResetToDefaultConfirm = useCallback(async () => {
    if (!getDefaultPermissions) return

    modal.confirm({
      title: t("permissions.editor.resetConfirmTitle", { role: roleName }),
      content: (
        <>
          <p className="m-0">
            {t("permissions.editor.resetConfirmBody", { role: roleName })}
          </p>
          <p className="m-0 mt-2 text-gray-500">
            {t("permissions.editor.resetConfirmSaveNote")}
          </p>
        </>
      ),
      okType: "danger",
      okText: t("permissions.editor.resetConfirmOk"),
      cancelText: t("permissions.editor.cancel"),
      onOk: async () => {
        try {
          const defaults = await getDefaultPermissions()
          if (defaults) {
            form.setFieldsValue(defaults)
            notification.success({
              message: t("permissions.editor.resetLoadedTitle"),
              description: t("permissions.editor.resetLoadedBody")
            })
            form.submit()
          }
        } catch (e) {
          console.error(e)
        }
      }
    })
  }, [getDefaultPermissions, modal, form, notification, t, roleName])

  useEffect(() => {
    if (permissions) {
      form.setFieldsValue(permissions)
    }
  }, [permissions, form])

  return (
    <Form
      form={form}
      layout="vertical"
      component={false}
      onFinish={onFinish}
      onValuesChange={handleValuesChange}
      disabled={saving || disabled}
    >
      <FormChangedWatcher
        onDirtyChange={setIsDirty}
        form={form}
        saving={saving}
      />

      {permissions ? (
        <PermissionsEditor
          disabled={disabled}
          permissions={permissions}
          onBulkChange={onBulkColumnChange}
          onFeaturesToggle={onFeaturesToggleChange}
          isUpdating={saving}
          roleName={roleName}
          roleUserCount={roleUserCount}
          headerAction={
            isSystem &&
            !disabled && (
              <Button
                danger
                icon={<ReloadOutlined />}
                disabled={saving}
                onClick={handleResetToDefaultConfirm}
              >
                {t("permissions.editor.resetRole")}
              </Button>
            )
          }
        />
      ) : (
        <Empty description={t("permissions.editor.noPermissions")} />
      )}

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white px-4 py-2.5">
        <div className="flex items-center gap-3">
          {onTransferOwnership && (
            <Button
              disabled={false}
              icon={<BackwardOutlined />}
              onClick={onTransferOwnership}
            >
              {t("permissions.editor.backToTransfer")}
            </Button>
          )}
          {isDirty && !disabled && (
            <span
              role="status"
              className="inline-flex items-center gap-2 text-sm font-medium text-amber-700"
            >
              <span aria-hidden className="size-2 rounded-full bg-amber-500" />
              {t("permissions.editor.unsavedChanges")}
            </span>
          )}
        </div>
        {!disabled && (
          <div className="flex items-center gap-2 sm:mr-8">
            <Button
              onClick={() => form.resetFields()}
              disabled={!isDirty}
              aria-label={t("permissions.editor.cancelChanges")}
            >
              {t("permissions.editor.cancel")}
            </Button>
            <Button
              type="primary"
              disabled={!isDirty}
              loading={saving}
              onClick={form.submit}
            >
              {t("permissions.editor.saveChanges")}
            </Button>
          </div>
        )}
      </div>
    </Form>
  )
}
